// src/components/Admin/UserContentCuration.js
import React, { useState, useEffect, useCallback } from 'react';

const UserContentCuration = () => {
  const [userContent, setUserContent] = useState([]);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [activeTab, setActiveTab] = useState('pending'); // 'pending', 'approved', 'flagged', 'rejected'
  const [sortBy, setSortBy] = useState('newest');
  const [typeFilter, setTypeFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [flagFilter, setFlagFilter] = useState('');
  const [previewItem, setPreviewItem] = useState(null);

  const contentTypes = ['image', 'portfolio', 'project', 'space', 'object', 'inspiration'];
  const flagReasons = ['inappropriate', 'spam', 'copyright', 'low-quality', 'off-topic', 'duplicate'];

  // Mock user content data - in reality would come from Firebase/database
  const mockUserContent = [
    {
      id: 'user_001',
      type: 'image',
      title: 'Minimalist Kitchen Design',
      description: 'Clean lines and natural materials create a serene cooking space.',
      imageUrl: '/api/placeholder/600/400',
      submittedBy: 'sarah.designer',
      userEmail: 'sarah@example.com',
      status: 'pending',
      category: 'interiors',
      tags: ['minimalist', 'kitchen', 'natural materials'],
      signals: ['clean', 'minimal', 'natural'],
      submittedAt: Date.now() - 86400000, // 1 day ago
      moderatedBy: null,
      moderatedAt: null,
      digitalCardScore: 0.85,
      qualityScore: 0.92,
      originalityScore: 0.78,
      flags: [],
      userReputation: 4.2,
      userSubmissions: 12
    },
    {
      id: 'user_002',
      type: 'portfolio',
      title: 'Brutalist Architecture Collection',
      description: 'A curated collection of concrete structures from the 1970s.',
      imageUrl: '/api/placeholder/600/400',
      submittedBy: 'arch.explorer',
      userEmail: 'explorer@example.com',
      status: 'flagged',
      category: 'architecture',
      tags: ['brutalist', 'concrete', '1970s', 'collection'],
      signals: ['brutalist', 'concrete', 'monumental', 'historical'],
      submittedAt: Date.now() - 172800000, // 2 days ago
      moderatedBy: null,
      moderatedAt: null,
      digitalCardScore: 0.73,
      qualityScore: 0.68,
      originalityScore: 0.45,
      flags: [
        { reason: 'duplicate', reportedBy: 'admin', timestamp: Date.now() - 86400000 },
        { reason: 'low-quality', reportedBy: 'user123', timestamp: Date.now() - 43200000 }
      ],
      userReputation: 3.8,
      userSubmissions: 45
    },
    {
      id: 'user_003',
      type: 'space',
      title: 'Home Studio Setup',
      description: 'Creative workspace with vintage furniture and modern lighting.',
      imageUrl: '/api/placeholder/600/400',
      submittedBy: 'creative.mind',
      userEmail: 'creative@example.com',
      status: 'approved',
      category: 'interiors',
      tags: ['studio', 'workspace', 'vintage', 'lighting'],
      signals: ['creative', 'productive', 'inspiring', 'eclectic'],
      submittedAt: Date.now() - 259200000, // 3 days ago
      moderatedBy: 'admin',
      moderatedAt: Date.now() - 172800000, // 2 days ago
      digitalCardScore: 0.91,
      qualityScore: 0.89,
      originalityScore: 0.94,
      flags: [],
      userReputation: 4.7,
      userSubmissions: 8,
      approvalReason: 'High quality, excellent composition, aligns with platform aesthetic'
    },
    {
      id: 'user_004',
      type: 'object',
      title: 'Handmade Ceramic Vase',
      description: 'Organic form inspired by Japanese pottery traditions.',
      imageUrl: '/api/placeholder/600/400',
      submittedBy: 'pottery.lover',
      userEmail: 'pottery@example.com',
      status: 'rejected',
      category: 'art',
      tags: ['ceramic', 'handmade', 'japanese', 'organic'],
      signals: ['artisanal', 'organic', 'traditional', 'minimal'],
      submittedAt: Date.now() - 345600000, // 4 days ago
      moderatedBy: 'admin',
      moderatedAt: Date.now() - 259200000, // 3 days ago
      digitalCardScore: 0.82,
      qualityScore: 0.71,
      originalityScore: 0.88,
      flags: [],
      userReputation: 4.1,
      userSubmissions: 23,
      rejectionReason: 'Image quality too low for platform standards'
    }
  ];

  // Load user content data
  const loadUserContent = useCallback(async () => {
    setIsLoading(true);
    try {
      // In reality, this would fetch from your backend/Firebase
      await new Promise(resolve => setTimeout(resolve, 500)); // Simulate API call
      setUserContent(mockUserContent);
      setMessage(`✅ Loaded ${mockUserContent.length} user submissions`);
    } catch (error) {
      console.error('Error loading user content:', error);
      setMessage('❌ Error loading user content data');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUserContent();
  }, [loadUserContent]);

  // Filter and sort user content
  const getFilteredContent = useCallback(() => {
    let filtered = [...userContent];
    
    // Filter by tab
    switch (activeTab) {
      case 'pending':
        filtered = filtered.filter(item => item.status === 'pending');
        break;
      case 'approved':
        filtered = filtered.filter(item => item.status === 'approved');
        break;
      case 'flagged':
        filtered = filtered.filter(item => item.status === 'flagged' || item.flags.length > 0);
        break;
      case 'rejected':
        filtered = filtered.filter(item => item.status === 'rejected');
        break;
    }
    
    // Apply type filter
    if (typeFilter) {
      filtered = filtered.filter(item => item.type === typeFilter);
    }
    
    // Apply user filter
    if (userFilter) {
      filtered = filtered.filter(item => 
        item.submittedBy.toLowerCase().includes(userFilter.toLowerCase()) ||
        item.userEmail.toLowerCase().includes(userFilter.toLowerCase())
      );
    }
    
    // Apply flag filter
    if (flagFilter) {
      filtered = filtered.filter(item => 
        item.flags.some(flag => flag.reason === flagFilter)
      );
    }
    
    // Apply sorting
    const sorted = filtered.sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return (b.submittedAt || 0) - (a.submittedAt || 0);
        case 'oldest':
          return (a.submittedAt || 0) - (b.submittedAt || 0);
        case 'quality':
          return (b.qualityScore || 0) - (a.qualityScore || 0);
        case 'digital-card':
          return (b.digitalCardScore || 0) - (a.digitalCardScore || 0);
        case 'user-reputation':
          return (b.userReputation || 0) - (a.userReputation || 0);
        case 'flags':
          return (b.flags?.length || 0) - (a.flags?.length || 0);
        default:
          return 0;
      }
    });
    
    return sorted;
  }, [userContent, activeTab, typeFilter, userFilter, flagFilter, sortBy]);

  const filteredContent = getFilteredContent();

  // Handle content actions
  const handleApproveItems = async (itemIds) => {
    if (itemIds.length === 0) {
      setMessage('❌ No items selected');
      return;
    }

    if (!window.confirm(`Approve ${itemIds.length} user submissions?`)) {
      return;
    }

    setIsLoading(true);
    try {
      console.log('Approving user content:', itemIds);
      setMessage(`✅ Would approve ${itemIds.length} submissions (backend API needed)`);
      setSelectedItems(new Set());
    } catch (error) {
      console.error('Error approving content:', error);
      setMessage('❌ Error approving content');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRejectItems = async (itemIds, reason = '') => {
    if (itemIds.length === 0) {
      setMessage('❌ No items selected');
      return;
    }

    const rejectionReason = reason || prompt('Reason for rejection (optional):');
    if (rejectionReason === null) return; // User cancelled

    if (!window.confirm(`Reject ${itemIds.length} submissions? This will notify the users.`)) {
      return;
    }

    setIsLoading(true);
    try {
      console.log('Rejecting user content:', itemIds, 'Reason:', rejectionReason);
      setMessage(`✅ Would reject ${itemIds.length} submissions (backend API needed)`);
      setSelectedItems(new Set());
    } catch (error) {
      console.error('Error rejecting content:', error);
      setMessage('❌ Error rejecting content');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFlagItems = async (itemIds, flagReason) => {
    if (itemIds.length === 0) {
      setMessage('❌ No items selected');
      return;
    }

    setIsLoading(true);
    try {
      console.log('Flagging user content:', itemIds, 'Reason:', flagReason);
      setMessage(`✅ Would flag ${itemIds.length} submissions as ${flagReason} (backend API needed)`);
      setSelectedItems(new Set());
    } catch (error) {
      console.error('Error flagging content:', error);
      setMessage('❌ Error flagging content');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResolveFlaggedItem = async (itemId, action) => {
    setIsLoading(true);
    try {
      console.log(`Resolving flagged item ${itemId} with action: ${action}`);
      setMessage(`✅ Would ${action} flagged item (backend API needed)`);
    } catch (error) {
      console.error('Error resolving flagged item:', error);
      setMessage('❌ Error resolving flagged item');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle item selection
  const handleItemSelect = (itemId) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(itemId)) {
      newSelected.delete(itemId);
    } else {
      newSelected.add(itemId);
    }
    setSelectedItems(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedItems.size === filteredContent.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(filteredContent.map(item => item.id)));
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return '#ffc107';
      case 'approved': return '#28a745';
      case 'flagged': return '#dc3545';
      case 'rejected': return '#6c757d';
      default: return '#6c757d';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return '⏳';
      case 'approved': return '✅';
      case 'flagged': return '🚩';
      case 'rejected': return '❌';
      default: return '❓';
    }
  };

  const getQualityColor = (score) => {
    if (score >= 0.8) return '#28a745';
    if (score >= 0.6) return '#ffc107';
    return '#dc3545';
  };

  const getReputationColor = (reputation) => {
    if (reputation >= 4.5) return '#28a745';
    if (reputation >= 4.0) return '#17a2b8';
    if (reputation >= 3.5) return '#ffc107';
    return '#dc3545';
  };

  return (
    <div style={{
      maxWidth: '1400px',
      margin: '2rem auto',
      padding: '2rem',
      backgroundColor: 'white',
      borderRadius: '12px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)'
    }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <div>
          <h2 style={{ margin: 0, color: '#333' }}>User Content Curation</h2>
          <p style={{ margin: '0.5rem 0 0', color: '#666', fontSize: '0.9rem' }}>
            Review and moderate user-submitted content
          </p>
        </div>
        
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <button
            onClick={() => window.location.href = '/admin'}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
          >
            ← Admin Dashboard
          </button>
          
          <button
            onClick={loadUserContent}
            disabled={isLoading}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#17a2b8',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
          >
            🔄 Refresh
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ 
        display: 'flex', 
        borderBottom: '2px solid #e9ecef',
        marginBottom: '2rem'
      }}>
        {[
          { id: 'pending', label: 'Pending Review', count: userContent.filter(c => c.status === 'pending').length },
          { id: 'flagged', label: 'Flagged Content', count: userContent.filter(c => c.status === 'flagged' || c.flags.length > 0).length },
          { id: 'approved', label: 'Approved', count: userContent.filter(c => c.status === 'approved').length },
          { id: 'rejected', label: 'Rejected', count: userContent.filter(c => c.status === 'rejected').length }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '1rem 2rem',
              backgroundColor: 'transparent',
              border: 'none',
              borderBottom: activeTab === tab.id ? '2px solid #007bff' : '2px solid transparent',
              color: activeTab === tab.id ? '#007bff' : '#666',
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: '600'
            }}
          >
            {tab.label} ({tab.count})
          </button>
        ))}
      </div>

      {/* Filters */}
      <div style={{ 
        display: 'flex', 
        gap: '1rem', 
        marginBottom: '1.5rem', 
        padding: '1rem', 
        backgroundColor: '#f8f9fa', 
        borderRadius: '8px',
        flexWrap: 'wrap'
      }}>
        <div style={{ flex: 1, minWidth: '150px' }}>
          <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.9rem', fontWeight: '600' }}>
            Content Type
          </label>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            style={{
              width: '100%',
              padding: '0.5rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '0.9rem'
            }}
          >
            <option value="">All Types</option>
            {contentTypes.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>
        
        <div style={{ flex: 1, minWidth: '150px' }}>
          <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.9rem', fontWeight: '600' }}>
            User Filter
          </label>
          <input
            type="text"
            value={userFilter}
            onChange={(e) => setUserFilter(e.target.value)}
            placeholder="Username or email"
            style={{
              width: '100%',
              padding: '0.5rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '0.9rem'
            }}
          />
        </div>
        
        {activeTab === 'flagged' && (
          <div style={{ flex: 1, minWidth: '150px' }}>
            <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.9rem', fontWeight: '600' }}>
              Flag Reason
            </label>
            <select
              value={flagFilter}
              onChange={(e) => setFlagFilter(e.target.value)}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '0.9rem'
              }}
            >
              <option value="">All Flags</option>
              {flagReasons.map(reason => (
                <option key={reason} value={reason}>{reason}</option>
              ))}
            </select>
          </div>
        )}
        
        <div style={{ flex: 1, minWidth: '150px' }}>
          <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.9rem', fontWeight: '600' }}>
            Sort By
          </label>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            style={{
              width: '100%',
              padding: '0.5rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '0.9rem'
            }}
          >
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
            <option value="quality">Quality Score</option>
            <option value="digital-card">Digital Card Match</option>
            <option value="user-reputation">User Reputation</option>
            {activeTab === 'flagged' && <option value="flags">Most Flagged</option>}
          </select>
        </div>
      </div>

      {/* Bulk Actions */}
      {(activeTab === 'pending' || activeTab === 'flagged') && (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: '1rem',
          padding: '1rem',
          backgroundColor: '#e9ecef',
          borderRadius: '6px'
        }}>
          <div>
            <button
              onClick={handleSelectAll}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.9rem',
                marginRight: '1rem'
              }}
            >
              {selectedItems.size === filteredContent.length ? 'Deselect All' : 'Select All'}
            </button>
            
            <span style={{ fontSize: '0.9rem', color: '#666' }}>
              {selectedItems.size} of {filteredContent.length} selected
            </span>
          </div>
          
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {activeTab === 'pending' && (
              <>
                <button
                  onClick={() => handleApproveItems(Array.from(selectedItems))}
                  disabled={selectedItems.size === 0 || isLoading}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: selectedItems.size > 0 ? '#28a745' : '#ccc',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: selectedItems.size > 0 ? 'pointer' : 'not-allowed',
                    fontSize: '0.9rem'
                  }}
                >
                  ✅ Approve Selected ({selectedItems.size})
                </button>
                
                <button
                  onClick={() => handleRejectItems(Array.from(selectedItems))}
                  disabled={selectedItems.size === 0 || isLoading}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: selectedItems.size > 0 ? '#dc3545' : '#ccc',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: selectedItems.size > 0 ? 'pointer' : 'not-allowed',
                    fontSize: '0.9rem'
                  }}
                >
                  ❌ Reject Selected ({selectedItems.size})
                </button>
                
                <select
                  onChange={(e) => e.target.value && handleFlagItems(Array.from(selectedItems), e.target.value)}
                  disabled={selectedItems.size === 0}
                  style={{
                    padding: '0.5rem',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: selectedItems.size > 0 ? 'pointer' : 'not-allowed',
                    fontSize: '0.9rem',
                    backgroundColor: selectedItems.size > 0 ? '#ffc107' : '#ccc'
                  }}
                >
                  <option value="">🚩 Flag as...</option>
                  {flagReasons.map(reason => (
                    <option key={reason} value={reason}>{reason}</option>
                  ))}
                </select>
              </>
            )}
            
            {activeTab === 'flagged' && (
              <>
                <button
                  onClick={() => handleApproveItems(Array.from(selectedItems))}
                  disabled={selectedItems.size === 0 || isLoading}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: selectedItems.size > 0 ? '#28a745' : '#ccc',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: selectedItems.size > 0 ? 'pointer' : 'not-allowed',
                    fontSize: '0.9rem'
                  }}
                >
                  ✅ Approve & Clear Flags ({selectedItems.size})
                </button>
                
                <button
                  onClick={() => handleRejectItems(Array.from(selectedItems))}
                  disabled={selectedItems.size === 0 || isLoading}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: selectedItems.size > 0 ? '#dc3545' : '#ccc',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: selectedItems.size > 0 ? 'pointer' : 'not-allowed',
                    fontSize: '0.9rem'
                  }}
                >
                  ❌ Reject Flagged ({selectedItems.size})
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Content Grid */}
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: '2rem', fontSize: '1.1rem', color: '#666' }}>
          Loading user content...
        </div>
      ) : (
        <div>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', 
            gap: '1.5rem' 
          }}>
            {filteredContent.map((content) => (
              <div key={content.id} style={{
                border: content.flags.length > 0 ? '2px solid #dc3545' : '1px solid #ddd',
                borderRadius: '8px',
                padding: '1.5rem',
                backgroundColor: content.flags.length > 0 ? '#fff5f5' : 'white',
                position: 'relative',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}>
                {/* Selection checkbox for pending and flagged */}
                {(activeTab === 'pending' || activeTab === 'flagged') && (
                  <input
                    type="checkbox"
                    checked={selectedItems.has(content.id)}
                    onChange={() => handleItemSelect(content.id)}
                    style={{
                      position: 'absolute',
                      top: '1rem',
                      left: '1rem',
                      transform: 'scale(1.2)',
                      zIndex: 1
                    }}
                  />
                )}

                {/* Status and Quality indicators */}
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center', 
                  marginBottom: '1rem',
                  marginTop: (activeTab === 'pending' || activeTab === 'flagged') ? '2rem' : '0'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ fontSize: '1.2rem' }}>
                      {getStatusIcon(content.status)}
                    </span>
                    <span style={{
                      backgroundColor: getStatusColor(content.status),
                      color: 'white',
                      padding: '0.25rem 0.5rem',
                      borderRadius: '12px',
                      fontSize: '0.75rem',
                      fontWeight: 'bold'
                    }}>
                      {content.status.toUpperCase()}
                    </span>
                  </div>
                  
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <span style={{
                      backgroundColor: getQualityColor(content.qualityScore),
                      color: 'white',
                      padding: '0.25rem 0.5rem',
                      borderRadius: '12px',
                      fontSize: '0.7rem',
                      fontWeight: 'bold'
                    }}>
                      Q: {(content.qualityScore * 100).toFixed(0)}%
                    </span>
                    
                    <span style={{
                      backgroundColor: getReputationColor(content.userReputation),
                      color: 'white',
                      padding: '0.25rem 0.5rem',
                      borderRadius: '12px',
                      fontSize: '0.7rem',
                      fontWeight: 'bold'
                    }}>
                      ⭐ {content.userReputation.toFixed(1)}
                    </span>
                    
                    <button
                      onClick={() => setPreviewItem(content)}
                      style={{
                        padding: '0.25rem 0.5rem',
                        backgroundColor: '#17a2b8',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.7rem'
                      }}
                    >
                      👁️ Preview
                    </button>
                  </div>
                </div>

                {/* Content image */}
                <div style={{ marginBottom: '1rem' }}>
                  <img 
                    src={content.imageUrl} 
                    alt={content.title}
                    style={{
                      width: '100%',
                      height: '200px',
                      objectFit: 'cover',
                      borderRadius: '6px',
                      backgroundColor: '#f8f9fa'
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
                
                {/* Content details */}
                <div style={{ marginBottom: '1rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <span style={{
                      backgroundColor: '#e9ecef',
                      color: '#495057',
                      padding: '0.25rem 0.5rem',
                      borderRadius: '12px',
                      fontSize: '0.75rem',
                      fontWeight: 'bold'
                    }}>
                      {content.type.toUpperCase()}
                    </span>
                    
                    <span style={{ fontSize: '0.8rem', color: '#666' }}>
                      DC Score: {(content.digitalCardScore * 100).toFixed(0)}%
                    </span>
                  </div>
                  
                  <h4 style={{ 
                    margin: '0 0 0.5rem 0', 
                    fontSize: '1rem', 
                    fontWeight: '600',
                    lineHeight: '1.3'
                  }}>
                    {content.title}
                  </h4>
                  
                  <p style={{ 
                    margin: '0 0 0.75rem 0', 
                    fontSize: '0.9rem', 
                    color: '#666',
                    lineHeight: '1.4',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}>
                    {content.description}
                  </p>
                  
                  <div style={{ fontSize: '0.8rem', color: '#888', marginBottom: '0.5rem' }}>
                    <strong>By:</strong> {content.submittedBy} • <strong>Category:</strong> {content.category}
                  </div>
                  
                  <div style={{ fontSize: '0.8rem', color: '#888', marginBottom: '0.5rem' }}>
                    <strong>User:</strong> {content.userSubmissions} submissions • Rep: {content.userReputation}/5.0
                  </div>
                  
                  {content.tags && content.tags.length > 0 && (
                    <div style={{ fontSize: '0.75rem', color: '#999', marginBottom: '0.5rem' }}>
                      <strong>Tags:</strong> {content.tags.slice(0, 4).join(', ')}
                      {content.tags.length > 4 && ' ...'}
                    </div>
                  )}
                  
                  {content.signals && content.signals.length > 0 && (
                    <div style={{ fontSize: '0.75rem', color: '#999', marginBottom: '0.5rem' }}>
                      <strong>Signals:</strong> {content.signals.slice(0, 4).join(', ')}
                      {content.signals.length > 4 && ' ...'}
                    </div>
                  )}
                  
                  {/* Flags display */}
                  {content.flags && content.flags.length > 0 && (
                    <div style={{ 
                      marginTop: '0.75rem',
                      padding: '0.5rem',
                      backgroundColor: '#fee',
                      borderRadius: '4px',
                      fontSize: '0.8rem'
                    }}>
                      <strong style={{ color: '#dc3545' }}>Flags ({content.flags.length}):</strong>
                      {content.flags.map((flag, index) => (
                        <div key={index} style={{ marginTop: '0.25rem', color: '#666' }}>
                          • {flag.reason} (by {flag.reportedBy}) - {new Date(flag.timestamp).toLocaleDateString()}
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Approval/Rejection reason display */}
                  {(content.approvalReason || content.rejectionReason) && (
                    <div style={{ 
                      marginTop: '0.75rem',
                      padding: '0.5rem',
                      backgroundColor: content.status === 'approved' ? '#e8f5e8' : '#f0f0f0',
                      borderRadius: '4px',
                      fontSize: '0.8rem'
                    }}>
                      <strong>{content.status === 'approved' ? 'Approval' : 'Rejection'} Reason:</strong>
                      <div style={{ marginTop: '0.25rem', color: '#666' }}>
                        {content.approvalReason || content.rejectionReason}
                      </div>
                    </div>
                  )}
                  
                  <div style={{ fontSize: '0.7rem', color: '#aaa', marginTop: '0.75rem' }}>
                    Submitted: {new Date(content.submittedAt).toLocaleDateString()}
                    {content.moderatedAt && (
                      <span> • Moderated: {new Date(content.moderatedAt).toLocaleDateString()}</span>
                    )}
                  </div>
                </div>
                
                {/* Action buttons */}
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  {activeTab === 'pending' && (
                    <>
                      <button
                        onClick={() => handleApproveItems([content.id])}
                        disabled={isLoading}
                        style={{
                          flex: 1,
                          padding: '0.5rem',
                          backgroundColor: '#28a745',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '0.8rem'
                        }}
                      >
                        ✅ Approve
                      </button>
                      
                      <button
                        onClick={() => handleRejectItems([content.id])}
                        disabled={isLoading}
                        style={{
                          flex: 1,
                          padding: '0.5rem',
                          backgroundColor: '#dc3545',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '0.8rem'
                        }}
                      >
                        ❌ Reject
                      </button>
                      
                      <select
                        onChange={(e) => e.target.value && handleFlagItems([content.id], e.target.value)}
                        style={{
                          padding: '0.5rem',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '0.8rem',
                          backgroundColor: '#ffc107'
                        }}
                      >
                        <option value="">🚩 Flag</option>
                        {flagReasons.map(reason => (
                          <option key={reason} value={reason}>{reason}</option>
                        ))}
                      </select>
                    </>
                  )}
                  
                  {activeTab === 'flagged' && (
                    <>
                      <button
                        onClick={() => handleResolveFlaggedItem(content.id, 'approve')}
                        disabled={isLoading}
                        style={{
                          flex: 1,
                          padding: '0.5rem',
                          backgroundColor: '#28a745',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '0.8rem'
                        }}
                      >
                        ✅ Approve & Clear
                      </button>
                      
                      <button
                        onClick={() => handleResolveFlaggedItem(content.id, 'reject')}
                        disabled={isLoading}
                        style={{
                          flex: 1,
                          padding: '0.5rem',
                          backgroundColor: '#dc3545',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '0.8rem'
                        }}
                      >
                        ❌ Reject
                      </button>
                    </>
                  )}
                  
                  <button
                    onClick={() => window.open(`mailto:${content.userEmail}`, '_blank')}
                    style={{
                      padding: '0.5rem',
                      backgroundColor: '#17a2b8',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '0.8rem'
                    }}
                  >
                    📧 Contact
                  </button>
                </div>
              </div>
            ))}
          </div>
          
          {filteredContent.length === 0 && (
            <div style={{ 
              textAlign: 'center', 
              padding: '3rem', 
              color: '#666',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px'
            }}>
              <h3 style={{ margin: '0 0 0.5rem 0' }}>
                {activeTab === 'pending' && 'No pending submissions'}
                {activeTab === 'flagged' && 'No flagged content'}
                {activeTab === 'approved' && 'No approved content'}
                {activeTab === 'rejected' && 'No rejected content'}
              </h3>
              <p style={{ margin: 0 }}>
                {activeTab === 'pending' 
                  ? 'No user submissions awaiting review' 
                  : activeTab === 'flagged'
                    ? 'No content has been flagged for review'
                    : `No ${activeTab} content yet`}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Content Preview Modal */}
      {previewItem && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '2rem',
            maxWidth: '800px',
            maxHeight: '61.2vh',
            overflow: 'auto',
            position: 'relative'
          }}>
            <button
              onClick={() => setPreviewItem(null)}
              style={{
                position: 'absolute',
                top: '1rem',
                right: '1rem',
                backgroundColor: 'transparent',
                border: 'none',
                fontSize: '1.5rem',
                cursor: 'pointer',
                color: '#666'
              }}
            >
              ✕
            </button>
            
            <h3 style={{ margin: '0 0 1rem 0', color: '#333' }}>Content Preview</h3>
            
            {/* User info header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1.5rem',
              padding: '1rem',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px'
            }}>
              <div>
                <div style={{ fontWeight: '600', fontSize: '1rem' }}>
                  {previewItem.submittedBy}
                </div>
                <div style={{ fontSize: '0.8rem', color: '#666' }}>
                  {previewItem.userEmail} • {previewItem.userSubmissions} submissions
                </div>
                <div style={{ fontSize: '0.8rem', color: '#666' }}>
                  Reputation: ⭐ {previewItem.userReputation}/5.0
                </div>
              </div>
              
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '0.8rem', color: '#666', marginBottom: '0.25rem' }}>
                  Quality Scores:
                </div>
                <div style={{ fontSize: '0.75rem' }}>
                  Quality: <span style={{ color: getQualityColor(previewItem.qualityScore) }}>
                    {(previewItem.qualityScore * 100).toFixed(0)}%
                  </span>
                </div>
                <div style={{ fontSize: '0.75rem' }}>
                  Digital Card: <span style={{ color: getQualityColor(previewItem.digitalCardScore) }}>
                    {(previewItem.digitalCardScore * 100).toFixed(0)}%
                  </span>
                </div>
                <div style={{ fontSize: '0.75rem' }}>
                  Originality: <span style={{ color: getQualityColor(previewItem.originalityScore) }}>
                    {(previewItem.originalityScore * 100).toFixed(0)}%
                  </span>
                </div>
              </div>
            </div>
            
            {/* Content preview */}
            <div style={{
              border: '1px solid #ddd',
              borderRadius: '8px',
              overflow: 'hidden',
              marginBottom: '1.5rem'
            }}>
              <img 
                src={previewItem.imageUrl} 
                alt={previewItem.title}
                style={{
                  width: '100%',
                  maxHeight: '272px',
                  objectFit: 'contain',
                  backgroundColor: '#f8f9fa'
                }}
              />
              
              <div style={{ padding: '1rem' }}>
                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
                  <span style={{
                    backgroundColor: '#e9ecef',
                    color: '#495057',
                    padding: '0.25rem 0.5rem',
                    borderRadius: '12px',
                    fontSize: '0.75rem',
                    fontWeight: 'bold'
                  }}>
                    {previewItem.type.toUpperCase()}
                  </span>
                  
                  <span style={{
                    backgroundColor: '#e3f2fd',
                    color: '#1976d2',
                    padding: '0.25rem 0.5rem',
                    borderRadius: '12px',
                    fontSize: '0.75rem',
                    fontWeight: 'bold'
                  }}>
                    {previewItem.category}
                  </span>
                </div>
                
                <h4 style={{ margin: '0 0 0.75rem 0', fontSize: '1.2rem', fontWeight: '600' }}>
                  {previewItem.title}
                </h4>
                
                <p style={{ margin: '0 0 1rem 0', fontSize: '0.95rem', color: '#666', lineHeight: '1.5' }}>
                  {previewItem.description}
                </p>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', fontSize: '0.85rem' }}>
                  <div>
                    <strong>Tags:</strong>
                    <div style={{ marginTop: '0.25rem' }}>
                      {previewItem.tags.map(tag => (
                        <span key={tag} style={{
                          display: 'inline-block',
                          backgroundColor: '#e9ecef',
                          padding: '0.2rem 0.4rem',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          marginRight: '0.25rem',
                          marginBottom: '0.25rem'
                        }}>
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <strong>Signals:</strong>
                    <div style={{ marginTop: '0.25rem' }}>
                      {previewItem.signals.map(signal => (
                        <span key={signal} style={{
                          display: 'inline-block',
                          backgroundColor: '#e3f2fd',
                          color: '#1976d2',
                          padding: '0.2rem 0.4rem',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          marginRight: '0.25rem',
                          marginBottom: '0.25rem'
                        }}>
                          {signal}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Flags if any */}
            {previewItem.flags && previewItem.flags.length > 0 && (
              <div style={{
                marginBottom: '1.5rem',
                padding: '1rem',
                backgroundColor: '#fff5f5',
                border: '1px solid #fecaca',
                borderRadius: '8px'
              }}>
                <h4 style={{ margin: '0 0 0.75rem 0', color: '#dc3545' }}>
                  🚩 Content Flags ({previewItem.flags.length})
                </h4>
                {previewItem.flags.map((flag, index) => (
                  <div key={index} style={{
                    padding: '0.5rem',
                    backgroundColor: 'white',
                    borderRadius: '4px',
                    marginBottom: '0.5rem',
                    fontSize: '0.85rem'
                  }}>
                    <div style={{ fontWeight: '600', color: '#dc3545' }}>
                      {flag.reason.charAt(0).toUpperCase() + flag.reason.slice(1)}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#666' }}>
                      Reported by {flag.reportedBy} on {new Date(flag.timestamp).toLocaleDateString()}
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {/* Moderation history */}
            <div style={{
              marginBottom: '1.5rem',
              padding: '1rem',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              fontSize: '0.85rem'
            }}>
              <h4 style={{ margin: '0 0 0.75rem 0' }}>Submission Details</h4>
              <div><strong>Submitted:</strong> {new Date(previewItem.submittedAt).toLocaleString()}</div>
              {previewItem.moderatedBy && (
                <div><strong>Moderated by:</strong> {previewItem.moderatedBy} on {new Date(previewItem.moderatedAt).toLocaleString()}</div>
              )}
              {previewItem.approvalReason && (
                <div style={{ marginTop: '0.5rem' }}>
                  <strong>Approval Reason:</strong> {previewItem.approvalReason}
                </div>
              )}
              {previewItem.rejectionReason && (
                <div style={{ marginTop: '0.5rem' }}>
                  <strong>Rejection Reason:</strong> {previewItem.rejectionReason}
                </div>
              )}
            </div>
            
            {/* Quick action buttons */}
            {(previewItem.status === 'pending' || previewItem.status === 'flagged') && (
              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                <button
                  onClick={() => {
                    handleApproveItems([previewItem.id]);
                    setPreviewItem(null);
                  }}
                  style={{
                    padding: '0.75rem 1.5rem',
                    backgroundColor: '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: '600'
                  }}
                >
                  ✅ Approve Content
                </button>
                
                <button
                  onClick={() => {
                    handleRejectItems([previewItem.id]);
                    setPreviewItem(null);
                  }}
                  style={{
                    padding: '0.75rem 1.5rem',
                    backgroundColor: '#dc3545',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: '600'
                  }}
                >
                  ❌ Reject Content
                </button>
                
                <button
                  onClick={() => window.open(`mailto:${previewItem.userEmail}`, '_blank')}
                  style={{
                    padding: '0.75rem 1.5rem',
                    backgroundColor: '#17a2b8',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: '600'
                  }}
                >
                  📧 Contact User
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Status Message */}
      {message && (
        <div style={{
          marginTop: '1rem',
          padding: '0.75rem',
          backgroundColor: message.includes('❌') ? '#fee' : '#efe',
          color: message.includes('❌') ? '#c33' : '#363',
          borderRadius: '6px',
          fontSize: '0.9rem'
        }}>
          {message}
        </div>
      )}

      {/* Note about backend API needed */}
      <div style={{
        marginTop: '2rem',
        padding: '1rem',
        backgroundColor: '#fff3cd',
        border: '1px solid #ffeaa7',
        borderRadius: '6px',
        fontSize: '0.85rem',
        color: '#856404'
      }}>
        <strong>Note:</strong> This interface shows the user content moderation workflow. You'll need a backend API to actually manage content approval, user notifications, and flag resolution. The approve/reject/flag actions currently just log what would happen.
      </div>
    </div>
  );
};

export default UserContentCuration;